import { 
  collection, 
  getDocs, 
  setDoc, 
  doc, 
  deleteDoc, 
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { Project, Career, CompanyStats, BrandingSettings, CarouselItem, Brand, AboutSettings, Speciality, ContactSettings } from './types';

const COLLECTIONS = {
  PROJECTS: 'projects',
  CAREERS: 'careers',
  STATS: 'stats',
  BRANDING: 'branding',
  ABOUT: 'about',
  CAROUSEL: 'carousel',
  INQUIRIES: 'inquiries',
  BRANDS: 'brands',
  SPECIALITIES: 'specialities',
  CONTACT: 'contact',
  USERS: 'users'
};

const handleFirestoreError = (error: any, action: string) => {
  const isWrite = action.startsWith('save') || action.startsWith('delete') || action.startsWith('approve');
  
  if (error.code === 'permission-denied') {
    console.warn(`Firestore Permission Denied [${action}]: Ensure rules allow read/write.`);
  } else {
    console.error(`Firestore Error [${action}]:`, error);
  }
  throw error;
};

export const dbService = {
  getProjects: async (): Promise<Project[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.PROJECTS));
      return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Project));
    } catch (error) {
      return handleFirestoreError(error, "getProjects");
    }
  },
  saveProject: async (project: Project) => {
    try {
      await setDoc(doc(db, COLLECTIONS.PROJECTS, project.id), project);
    } catch (error) {
      handleFirestoreError(error, "saveProject");
    }
  },
  deleteProject: async (id: string) => {
    if (!id) return;
    try {
      await deleteDoc(doc(db, COLLECTIONS.PROJECTS, id));
    } catch (error) {
      handleFirestoreError(error, "deleteProject");
    }
  },
  getCareers: async (): Promise<Career[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.CAREERS));
      return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Career));
    } catch (error) {
      return handleFirestoreError(error, "getCareers");
    }
  },
  saveCareer: async (career: Career) => {
    try {
      await setDoc(doc(db, COLLECTIONS.CAREERS, career.id), career);
    } catch (error) {
      handleFirestoreError(error, "saveCareer");
    }
  },
  deleteCareer: async (id: string) => {
    if (!id) return;
    try {
      await deleteDoc(doc(db, COLLECTIONS.CAREERS, id));
    } catch (error) {
      handleFirestoreError(error, "deleteCareer");
    }
  },
  getCarousel: async (): Promise<CarouselItem[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.CAROUSEL));
      return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as CarouselItem));
    } catch (error) {
      return handleFirestoreError(error, "getCarousel");
    }
  },
  saveCarousel: async (item: CarouselItem) => {
    try {
      await setDoc(doc(db, COLLECTIONS.CAROUSEL, item.id), item);
    } catch (error) {
      handleFirestoreError(error, "saveCarousel");
    }
  },
  deleteCarousel: async (id: string) => {
    if (!id) return;
    try {
      await deleteDoc(doc(db, COLLECTIONS.CAROUSEL, id));
    } catch (error) {
      handleFirestoreError(error, "deleteCarousel");
    }
  },
  getStats: async (): Promise<CompanyStats | null> => {
    try {
      const docSnap = await getDoc(doc(db, COLLECTIONS.STATS, 'company_stats'));
      return docSnap.exists() ? docSnap.data() as CompanyStats : null;
    } catch (error) {
      return handleFirestoreError(error, "getStats");
    }
  },
  saveStats: async (stats: CompanyStats) => {
    try {
      await setDoc(doc(db, COLLECTIONS.STATS, 'company_stats'), stats);
    } catch (error) {
      handleFirestoreError(error, "saveStats");
    }
  },
  getBranding: async (): Promise<BrandingSettings | null> => {
    try {
      const docSnap = await getDoc(doc(db, COLLECTIONS.BRANDING, 'settings'));
      return docSnap.exists() ? docSnap.data() as BrandingSettings : null;
    } catch (error) {
      return handleFirestoreError(error, "getBranding");
    }
  },
  saveBranding: async (branding: BrandingSettings) => {
    try {
      await setDoc(doc(db, COLLECTIONS.BRANDING, 'settings'), branding);
    } catch (error) {
      handleFirestoreError(error, "saveBranding");
    }
  },
  getAbout: async (): Promise<AboutSettings | null> => {
    try {
      const docSnap = await getDoc(doc(db, COLLECTIONS.ABOUT, 'settings'));
      return docSnap.exists() ? docSnap.data() as AboutSettings : null;
    } catch (error) {
      return handleFirestoreError(error, "getAbout");
    }
  },
  saveAbout: async (about: AboutSettings) => {
    try {
      await setDoc(doc(db, COLLECTIONS.ABOUT, 'settings'), about);
    } catch (error) {
      handleFirestoreError(error, "saveAbout");
    }
  },
  saveInquiry: async (inquiry: any) => {
    try {
      await addDoc(collection(db, COLLECTIONS.INQUIRIES), {
        ...inquiry,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      handleFirestoreError(error, "saveInquiry");
    }
  },
  getInquiries: async () => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.INQUIRIES));
      return querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() as any }))
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    } catch (error) {
      return handleFirestoreError(error, "getInquiries");
    }
  },
  deleteInquiry: async (id: string) => {
    if (!id) return;
    try {
      await deleteDoc(doc(db, COLLECTIONS.INQUIRIES, id));
    } catch (error) {
      handleFirestoreError(error, "deleteInquiry");
    }
  },
  saveBrand: async (brand: Brand) => {
    try {
      await setDoc(doc(db, COLLECTIONS.BRANDS, brand.id), brand);
    } catch (error) {
      handleFirestoreError(error, "saveBrand");
    }
  },
  getBrands: async (): Promise<Brand[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.BRANDS));
      return querySnapshot.docs
        .map(doc => ({ ...doc.data(), id: doc.id } as Brand))
        .sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime());
    } catch (error) {
      return handleFirestoreError(error, "getBrands");
    }
  },
  deleteBrand: async (id: string) => {
    if (!id) return;
    try {
      await deleteDoc(doc(db, COLLECTIONS.BRANDS, id));
    } catch (error) {
      handleFirestoreError(error, "deleteBrand");
    }
  },
  getSpecialities: async (): Promise<Speciality[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.SPECIALITIES));
      return querySnapshot.docs.map(doc => doc.data() as Speciality);
    } catch (error) {
      return handleFirestoreError(error, "getSpecialities");
    }
  },
  saveSpeciality: async (speciality: Speciality) => {
    try {
      await setDoc(doc(db, COLLECTIONS.SPECIALITIES, speciality.id), speciality);
    } catch (error) {
      handleFirestoreError(error, "saveSpeciality");
    }
  },
  deleteSpeciality: async (id: string) => {
    if (!id) return;
    try {
      await deleteDoc(doc(db, COLLECTIONS.SPECIALITIES, id));
    } catch (error) {
      handleFirestoreError(error, "deleteSpeciality");
    }
  },
  getContact: async (): Promise<ContactSettings | null> => {
    try {
      const docSnap = await getDoc(doc(db, COLLECTIONS.CONTACT, 'settings'));
      return docSnap.exists() ? docSnap.data() as ContactSettings : null;
    } catch (error) {
      return handleFirestoreError(error, "getContact");
    }
  },
  saveContact: async (contact: ContactSettings) => {
    try {
      await setDoc(doc(db, COLLECTIONS.CONTACT, 'settings'), contact);
    } catch (error) {
      handleFirestoreError(error, "saveContact");
    }
  },
  getUserRecord: async (uid: string): Promise<any | null> => {
    try {
      const docSnap = await getDoc(doc(db, COLLECTIONS.USERS, uid));
      return docSnap.exists() ? docSnap.data() : null;
    } catch (error) {
      return handleFirestoreError(error, "getUserRecord");
    }
  },
  saveUserRecord: async (user: any) => {
    try {
      await setDoc(doc(db, COLLECTIONS.USERS, user.uid), user);
    } catch (error) {
      handleFirestoreError(error, "saveUserRecord");
    }
  },
  getAllUsers: async (): Promise<any[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
      return querySnapshot.docs.map(doc => doc.data());
    } catch (error) {
      return handleFirestoreError(error, "getAllUsers");
    }
  },
  updateUserApproval: async (uid: string, isApproved: boolean) => {
    try {
      await updateDoc(doc(db, COLLECTIONS.USERS, uid), { isApproved });
    } catch (error) {
      handleFirestoreError(error, "updateUserApproval");
    }
  }
};